/**
 * Format a date as a short relative-time string (e.g. "2h ago", "3d ago").
 *
 * Returns null when the input is null/undefined/invalid so callers can
 * decide what fallback to show ("No activity", etc.).
 *
 * Buckets:
 *   < 60 s             → "just now"
 *   < 60 min           → "Xm ago"
 *   < 24 h             → "Xh ago"
 *   ≤ 30 d             → "Xd ago"
 *   < 365 d            → "Xmo ago"
 *   ≥ 365 d            → "Xy ago"
 *
 * Future dates (clock drift, etc.) are treated as "just now".
 */
export function formatRelativeTime(
  date: string | Date | null | undefined,
): string | null {
  if (date === null || date === undefined) return null;

  const d = date instanceof Date ? date : new Date(date as string);
  if (isNaN(d.getTime())) return null;

  const diffMs = Date.now() - d.getTime();

  // Future or within 60 seconds
  if (diffMs < 60_000) return 'just now';

  const diffSec = Math.floor(diffMs / 1_000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays <= 30) return `${diffDays}d ago`;
  if (diffYears < 1) return `${diffMonths}mo ago`;
  return `${diffYears}y ago`;
}
